import { useEffect, useRef, useState, useCallback } from "react";
import { gsap } from "gsap";
import {
    SLIDES,
    TOTAL_SLIDES,
    MOBILE_SLOTS,
    MOBILE_SLOT_ORDER,
    MOBILE_OFFSETS,
    MOBILE_NEXT_MOVES,
    MOBILE_PREV_MOVES,
    SWIPE_THRESHOLD,
    AUTOPLAY_INTERVAL,
} from "./constant";
import { SlideImage, TitleDescription } from "./reusableComponent";

const HistoryMobile = () => {
    const slotRefs = useRef([]);
    const swipeStartX = useRef(0);
    const isSwiping = useRef(false);

    const yearRef = useRef(null);
    const titleRef = useRef(null);
    const descRef = useRef(null);

    const [currentIndex, setCurrentIndex] = useState(0);
    const [animating, setAnimating] = useState(false);

    const resetSlots = () => {
        MOBILE_SLOT_ORDER.forEach((position, i) => {
            if (slotRefs.current[i]) gsap.set(slotRefs.current[i], position);
        });
    };

    useEffect(() => { resetSlots(); }, []);

    const navigate = useCallback((dir) => {
        if (animating) return;
        if (dir === 1 && currentIndex >= TOTAL_SLIDES - 1) return;
        if (dir === -1 && currentIndex <= 0) return;

        setAnimating(true);

        const textElements = [yearRef.current, titleRef.current, descRef.current];
        const moves = dir === 1 ? MOBILE_NEXT_MOVES : MOBILE_PREV_MOVES;

        const timeline = gsap.timeline({
            onComplete: () => {
                setCurrentIndex((prev) => prev + dir);
                requestAnimationFrame(() => {
                    resetSlots();
                    gsap.fromTo(
                        textElements,
                        { opacity: 0, y: dir * 10 },
                        { opacity: 1, y: 0, duration: 0.3, ease: "power2.out", onComplete: () => setAnimating(false) },
                    );
                });
            },
        });

        timeline.to(textElements, { opacity: 0, y: dir * -6, duration: 0.2, ease: "power2.in" }, 0);

        moves.forEach(({ slotIndex, target }) => {
            if (slotRefs.current[slotIndex]) {
                timeline.to(slotRefs.current[slotIndex], { ...MOBILE_SLOTS[target], duration: 0.5, ease: "power2.inOut" }, 0);
            }
        });
    }, [animating, currentIndex]);

    const onPointerDown = (e) => {
        isSwiping.current = true;
        swipeStartX.current = e.clientX;
    };

    const onPointerUp = (e) => {
        if (!isSwiping.current) return;
        isSwiping.current = false;
        const distance = e.clientX - swipeStartX.current;
        if (Math.abs(distance) >= SWIPE_THRESHOLD) navigate(distance < 0 ? 1 : -1);
    };

    useEffect(() => {
        const intervalId = setInterval(() => {
            if (!isSwiping.current && !animating && currentIndex < TOTAL_SLIDES - 1) navigate(1);
        }, AUTOPLAY_INTERVAL);
        return () => clearInterval(intervalId);
    }, [navigate, currentIndex, animating]);

    const slide = SLIDES[currentIndex];

    return (
        <div
            onPointerDown={onPointerDown}
            onPointerUp={onPointerUp}
            className="historyMobileStage"
        >
            <div className="historyMobileInner">
                <div className="historyMobileCarousel">
                    {MOBILE_OFFSETS.map((offset, slotIndex) => {
                        const slideIndex = currentIndex + offset;
                        const isValidSlide = slideIndex >= 0 && slideIndex < TOTAL_SLIDES;

                        return (
                            <div
                                key={offset}
                                ref={(el) => (slotRefs.current[slotIndex] = el)}
                                className="historyMobileSlot"
                            >
                                {isValidSlide && (
                                    <SlideImage
                                        src={SLIDES[slideIndex].imgSrc}
                                        alt={SLIDES[slideIndex].imgAlt}
                                        className="historyCardImg"
                                    />
                                )}
                            </div>
                        );
                    })}
                </div>

                <div className="historyMobileTextBlock">
                    <TitleDescription
                        year={slide.year}
                        yearClassName="historyMobileYear"
                        title={slide.title}
                        description={slide.description}
                        yearRef={yearRef}
                        titleRef={titleRef}
                        descRef={descRef}
                        titleClassName="historyMobileTitleText"
                        descClassName="historyMobileDescText"
                    />
                </div>
            </div>
        </div>
    );
};

export default HistoryMobile;
